import { CharacterInputValue } from '@tencent/tds-ui';
import { TdsFieldSelectorEnableExcludeValue } from '@tencent/tds-ui/src/query-form';
import { trpc } from '@tencent/tpro_rmonitor_metrics_server_get_metrics';
import { isArray, isEmpty } from 'lodash';
import { isNil, parseInt } from 'lodash-es';
import moment from 'moment';
import { bytesToKb, kbToBytes } from '@/infrastructure/format-data';
import { CommonDateRange } from '@/infrastructure/utils/timestamp';
import { MatchMode } from '@/presentation/components/form/standards/types';
import { TdsFormItem, TdsFormItemType } from '@/presentation/components/tds-form/form';
import { TdsCharacterRangeValue } from '@/presentation/components/tds-form/tds-character-range';
import { getInitTimeRange, TdsDateCascaderValue } from '@/presentation/components/tds-form/tds-date-cascader';
import { ITimingKey } from '@/presentation/pages/mini-program/types';
import { IFilter, TimeType } from '@/proto/metric';

export type KeyDataType = trpc.rmonitor.common.KeyDataType;
export const { KeyDataType } = trpc.rmonitor.common;

const FIELD_ALL = '';

export const CUSTOM = 'custom';

export const shortRanges: Record<string, any> = {
  ['近1小时']: () => CommonDateRange.hour(),
  ['今天']: () => CommonDateRange.today(),
  ['昨天']: () => CommonDateRange.yesterday(),
  ['近7天']: () => CommonDateRange.lastDaysFull(7),
  ['近30天']: () => CommonDateRange.lastDaysFull(30),
  ['近45天']: () => CommonDateRange.lastDaysFull(45),
  ['近90天']: () => CommonDateRange.lastDaysFull(90),
  ['近180天']: () => CommonDateRange.lastDaysFull(180),
};

export const dynamicGenTimeRange = (value: TdsDateCascaderValue): TdsDateCascaderValue => {
  const { short_cut: shortCut } = value;

  // 非自定义模式
  if (shortCut !== CUSTOM) {
    return {
      ...value,
      date_value: shortRanges[shortCut as string](),
    };
  }

  // 自定义模式不处理
  return value;
};

export const toArray = (value: string | string[]) => {
  if (Array.isArray(value)) {
    return value;
  }
  if (typeof value === 'string') {
    return value ? [value] : [];
  }

  return value ? [value] : [];
};

export const isValidInputValue = (item: CharacterInputValue) => {
  const values = (item.values || []) as string[];

  if (item?.mode === MatchMode.Between) {
    return !isEmpty(values);
  }

  return item
    && !isEmpty(item)
    && ([
      MatchMode.ISEMPTY,
      MatchMode.NOTEMPTY,
    ].includes(item.mode as MatchMode) || (!values?.includes(FIELD_ALL)
      && (values?.length) > 0));
};

export const isValidFilter = (item: IFilter) => {
  const values = (item.values || []) as string[];
  return item
    && !isEmpty(item)
    && ([
      MatchMode.ISEMPTY,
      MatchMode.NOTEMPTY,
    ].includes(item.operator as MatchMode) || (!values.includes(FIELD_ALL)
      && (values.length) > 0));
};

/**
 * 时间范围转filter格式
 * @param value
 */
export const dateCascader2Filter = (value: TdsDateCascaderValue): IFilter[] => {
  const [startTime, endTime] = value.date_value || [];

  if (startTime && endTime) {
    return [
      {
        name: value.date_type,
        values: [`${moment(startTime).unix()}`],
        operator: 'GE',
      },
      {
        name: value.date_type,
        values: [`${moment(endTime).unix()}`],
        operator: 'LE',
      },
    ];
  }

  return [];
};

export const standardVersion2Filter = (filterKey: string, value: CharacterInputValue): IFilter[] => {
  const operator = value?.mode;
  if (operator === MatchMode.Between) {
    const { min, max } = (value?.values ?? {}) as TdsCharacterRangeValue;
    const versionRangeValueStr = (min && max)
      ? [`${min}`, `${max}`] : '';

    return [{
      name: filterKey,
      values: toArray(versionRangeValueStr),
      operator,
    }];
  }
  const values = toArray(value?.values as string);
  return [{
    name: filterKey,
    values,
    operator: operator || '',
  }];
};

export const version2Filter = (filterKey: string, value: CharacterInputValue): IFilter[] => {
  const operator = value?.mode;
  if (operator === MatchMode.Between) {
    const { min, max } = (value?.values ?? {}) as TdsCharacterRangeValue;
    const versionRangeValueStr = (min && max)
      ? [`${min},${max}`] : '';

    return [{
      name: filterKey,
      values: toArray(versionRangeValueStr),
      operator,
    }];
  }
  const values = toArray(value?.values as string);
  return [{
    name: filterKey,
    values,
    operator: operator || '',
  }];
};

export const enableExcludeFields2Filter = (filterKey: string, value: TdsFieldSelectorEnableExcludeValue): IFilter[] => {
  const operator = !value?.switch_value ? 'OR' : 'NOT';
  const values = toArray(value?.select_value as string);
  return [{
    name: filterKey,
    values,
    operator: operator || '',
  }];
};

export const number2Filter = (field: string, value: CharacterInputValue): IFilter[] => {
  // 区间模式
  if (value.mode === MatchMode.Between) {
    const { min, max } = value?.values ?? {};
    const values = (min && max) ? `${min},${max}` : '';
    return [
      {
        name: field,
        values: toArray(values),
        operator: value.mode,
      },
    ];
  }

  return [
    {
      name: field,
      values: toArray((`${value?.values ?? ''}`) as string),
      operator: value.mode,
    },
  ];
};

export const simpleField2Filter = (field: string, value: string): IFilter[] => {
  const trimValue = value?.trim();
  if (!trimValue) return [];

  return value === 'launch' || value === 'routeChange' ? [
    {
      name: field,
      values: ['performance'],
      operator: MatchMode.OR,
    },
    {
      name: 'page_load_strategy',
      values: toArray(value),
      operator: MatchMode.OR,
    },
  ] : [
    {
      name: field,
      values: toArray(value),
      operator: MatchMode.OR,
    },
  ];
};

export const enumFields2Filter = (field: string, value: string[]): IFilter[] => {
  if (!value.length || !field) return [];
  return [
    {
      name: field,
      values: toArray(value),
      operator: MatchMode.OR,
    },
  ];
};


/**
 * key=>value 转filter格式
 * @param field
 * @param value
 */
export const field2Filter = (field: string, value: CharacterInputValue): IFilter[] => {
  if (!isValidInputValue(value)) return [];

  switch (value.mode) {
    case MatchMode.ISEMPTY:
    case MatchMode.NOTEMPTY:
      return [
        {
          name: field,
          values: [],
          operator: value.mode,
        },
      ];
    default:
      return [
        {
          name: field,
          values: toArray((value.values ?? []) as string[]),
          operator: value.mode,
        },
      ];
  }
};

export const dateCascaderConverter = (value: TdsDateCascaderValue) => {
  // 动态根据快捷方式计算
  const timeRange = getInitTimeRange(value?.time_window_type as TimeType, value?.short_cut as string);
  if (value?.short_cut !== CUSTOM) {
    return {
      ...value,
      date_value: [
        timeRange?.[0],
        timeRange?.[1],
      ],
    };
  }

  return {
    ...value,
    date_value: [
      value?.date_value?.[0] ? moment(value?.date_value?.[0]) : undefined,
      value?.date_value?.[1] ? moment(value?.date_value?.[1]) : undefined,
    ],
  };
};

export const dateCascaderTransformer = (value: TdsDateCascaderValue) => {
  // 动态根据快捷方式计算
  const timeRange = getInitTimeRange(value?.time_window_type as TimeType, value?.short_cut as string);
  if (value?.short_cut !== CUSTOM) {
    return {
      ...value,
      date_value: [
        timeRange?.[0]?.unix(),
        timeRange?.[1]?.unix(),
      ],
    };
  }

  return {
    ...value,
    date_value: [
      value?.date_value?.[0] ? moment(value?.date_value?.[0]).unix() : undefined,
      value?.date_value?.[1] ? moment(value?.date_value?.[1]).unix() : undefined,
    ],
  };
};

/**
 * 自定义字段的值转filter
 */
export const customFields2Filter = (
  field: string,
  value: CharacterInputValue,
  key?: string,
  type?: KeyDataType,
): IFilter[] => {
  if (value?.mode === MatchMode.Between) {
    const { min, max } = (value?.values ?? {}) as TdsCharacterRangeValue;
    const versionRangeValueStr = (min && max)
      ? [`${min},${max}`] : '';
    return [{
      name: field,
      key,
      values: toArray(versionRangeValueStr),
      type: type || KeyDataType.ARRAY,
      // @ts-ignore
      operator: value?.mode ?? MatchMode.OR,
    }];
  }

  return [
    {
      name: field,
      key,
      values: toArray((value?.values ?? '') as string[]),
      type: KeyDataType.ARRAY,
      operator: value?.mode ?? MatchMode.OR,
    },
  ];
};

/**
 * 自定义字段的值转filter
 */
export const customFieldsDuration2Filter = (
  field: string,
  value: any,
  key?: string,
  type?: KeyDataType,
): IFilter[] => {
  // @ts-ignore
  if (value?.operator === MatchMode.Between) {
    const { min, max } = (value?.values ?? {}) as TdsCharacterRangeValue;
    const versionRangeValueStr = (min && max)
      ? [`${min},${max}`] : '';
    return [{
      name: field,
      key,
      values: toArray(versionRangeValueStr),
      type,
      // @ts-ignore
      operator: value?.operator ?? MatchMode.OR,
    }];
  }

  return [
    {
      name: field,
      key,
      values: toArray(`${value?.values ?? ''}`),
      type,
      operator: value?.operator ?? MatchMode.OR,
    },
  ];
};

/**
 * 自定义字段的值转filter（name用key，key赋0）
 */
export const customFieldsDuration2FilterWithNameKeySwap = (
  _field: string,
  value: any,
  key?: string,
  _type?: KeyDataType,
): IFilter[] => {
  if (value?.operator === MatchMode.Between) {
    const { min, max } = (value?.values ?? {}) as TdsCharacterRangeValue;
    const versionRangeValueStr = (min && max)
      ? [`${min},${max}`] : '';
    return [{
      name: key, // 用key作为name
      key: null,    // key赋null
      values: toArray(versionRangeValueStr),
      type: 0,
      operator: value?.operator ?? MatchMode.OR,
    }];
  }

  return [
    {
      name: key, // 用key作为name
      key: null,    // key赋0
      values: toArray(`${value?.values ?? ''}`),
      type: 0,
      operator: value?.operator ?? MatchMode.OR,
    },
  ];
};

/**
 * 自定义字段的值转filter
 */
export const customFieldsDuration2FilterNew = (
  field: string,
  value: any,
  key?: string,
  type?: KeyDataType,
): IFilter[] => {
  // @ts-ignore
  if (value?.operator === MatchMode.Between) {
    const { min, max } = (value?.values ?? {}) as TdsCharacterRangeValue;
    const versionRangeValueStr = (min && max)
      ? [min, max] : '';
    return [{
      name: field,
      key,
      values: toArray(versionRangeValueStr),
      type,
      // @ts-ignore
      operator: value?.operator ?? MatchMode.OR,
    }];
  }

  return [
    {
      name: field,
      key,
      values: toArray(`${value?.values ?? ''}`),
      type,
      operator: value?.operator ?? MatchMode.OR,
    },
  ];
};


/**
 * 自定义
 */
export const customFilter = (
  field: string,
  value: CharacterInputValue, key?: string, type?: KeyDataType,
): IFilter[] => [
  {
    name: field,
    key,
    values: toArray((value?.values ?? '') as string[]),
    type,
    operator: value?.mode ?? MatchMode.OR,
  },
];

/**
 * 将数据大小字段的kb转换成bytes
 */
export const fieldValue2Bytes = (value: any) => {
  if (value.name === ITimingKey.TransferSize) {
    // eslint-disable-next-line no-param-reassign
    value.values = value.operator === MatchMode.Between
      ? {
        max: kbToBytes(value.values?.max),
        min: kbToBytes(value.values?.min),
      } : kbToBytes(value.values);
  }
  return value;
};

/**
 * 将数据大小字段的bytes转换成kb
 */
export const fieldValue2Kb = (value: any) => {
  if (!isArray(value)) return value;

  return value.map((item) => {
    if (item.name === ITimingKey.TransferSize) {
      return ({
        ...item,
        values: item.operator === MatchMode.Between
          ? {
            max: bytesToKb(item.values?.max),
            min: bytesToKb(item.values?.min),
          } : bytesToKb(item.values),
      });
    }
    return item;
  });
};

export const standardFieldValue2Kb = (fieldKey: string, value: CharacterInputValue) => {
  const operator = value?.mode;
  if (operator === MatchMode.Between) {
    const { min, max } = (value?.values ?? {}) as TdsCharacterRangeValue;
    const bytesMin = kbToBytes(parseInt(min || '', 10));
    const bytesMax = kbToBytes(parseInt(max || '', 10));
    const versionRangeValueStr = (min && max)
      ? [`${bytesMin}`, `${bytesMax}`] : '';

    return [{
      name: fieldKey,
      values: toArray(versionRangeValueStr),
      operator,
    }];
  }

  const bytesValue = kbToBytes(value?.values ?? 0);
  const values = toArray(`${bytesValue}`);
  return [{
    name: fieldKey,
    values,
    operator: operator || '',
  }];
};

// 过滤表单没有value值的字段
export const filterFormValues = (
  formItems: TdsFormItem[],
  formValues: Record<string, any>,
) => formItems?.filter((entry) => {
  if (isNil(formValues?.[String(entry?.name)])) {
    return false;
  }
  switch (entry.type) {
    case TdsFormItemType.CharacterInput:
      return !isNil(formValues?.[String(entry?.name)]?.values)
        && formValues?.[String(entry?.name)]?.values?.length !== 0;
    case TdsFormItemType.FieldSelector:
      return !!formValues?.[String(entry?.name)]?.length;
    case TdsFormItemType.DateCascader:
      return !isNil(formValues?.[String(entry?.name)]?.value?.date_value);
    case TdsFormItemType.NumberInput:
      return !isNil(formValues?.[String(entry?.name)]?.values)
        && formValues?.[String(entry?.name)]?.values?.length !== 0;
    case TdsFormItemType.CustomRange:
      return !isNil(formValues?.[String(entry?.name)]?.values)
        && formValues?.[String(entry?.name)]?.values?.length !== 0;
    case TdsFormItemType.CustomFields:
      return !isNil(formValues?.[String(entry?.name)]);
    case TdsFormItemType.FieldInputNumber:
      return !isNil(formValues?.[String(entry?.name)]);
    case TdsFormItemType.FieldInput:
      return !isNil(formValues?.[String(entry?.name)]);
    case TdsFormItemType.FieldSelectorEnableExclude:
      return !isNil(formValues?.[String(entry?.name)]) && formValues?.[String(entry?.name)]?.select_value?.length > 0;
    case TdsFormItemType.CustomPanel:
      return !isNil(formValues?.[String(entry?.name)]) && formValues?.[String(entry?.name)]?.length > 0;
    default:
      return !isNil(formValues?.[String(entry?.name)]);
  }
})?.map?.(item => String(item.name));


// mb范围转字节
export const mbFilter = (name: string, operator: string, min: number, max: number) => {
  if (!min && !max) {
    return [];
  }
  return [
    {
      name,
      operator,
      values: [`${min ? Math.round(min * 1024 * 1024) : ''},${max ? (max * 1024 * 1024) : ''}`],
    },
  ];
};
